import { useQuery } from '@apollo/client';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import {
  BillingDocPage,
  ErrorComponents,
  getAdaBaseURL,
  useFeatureToggle,
  useRecentlyViewed,
  useTitle,
} from '@css-xp/base';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { EntityWithNotes } from '@atlassian/cssxp-types-audit';
import { InvoiceWithLineItems } from '@atlassian/cssxp-types-billing';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  getInvoiceActions,
  getInvoiceAdditionalDetails,
  getInvoiceBreadcrumbTrail,
  getInvoiceHeader,
  getInvoiceIcon,
  getInvoiceSubheader,
  getViewInAdaInvoiceUrl,
} from '../invoice-page-utils';
import { ENABLE_RECENTLY_VISITED_HISTORY } from '@atlassian/cssxp-utils-generic';
import { fetchInvoiceQuery } from '../queries/fetch-invoice-query';
import { InvoiceRefundCcpPane } from '../sidebar/invoice-refund-ccp-pane';
import InvoiceLineItems from './invoice-line-items';
import InvoicePdfLinkPane from './invoice-pdf-link-pane';
import InvoiceUpdatePane from './invoice-update-pane';
import InvoiceUncollectiblePane from './invoice-uncollectible-pane';
import { ProcessRefundPane } from './process-refund/process-refund-pane';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { UFOEventSource } from '@atlassian/temp-observability';

interface InvoicePageProps {
  title?: string;
}

const InvoicePage = ({ title }: InvoicePageProps): JSX.Element | null => {
  const { id } = useParams<{ id: string }>();
  const [isEnabled] = useFeatureToggle();
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(id, title);
  }
  // TODO:  Need to update it further for presence hook but using redux logic once user profile service done
  //const { viewers } = usePresence('invoice', id);
  //const previousId = usePrevious(id);
  const { data, loading, error, refetch } = useQuery(fetchInvoiceQuery, {
    variables: {
      id,
    },
    fetchPolicy: 'no-cache',
  });
  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          invoiceNumber: data.invoice.id,
          fullName: data.invoice.billTo.fullName,
        },
      });
    }
  }, [data, title]);

  // to track the page view of the invoice Page
  GlobalPageLoadExperience.startPageLoad(UFOEventSource.INVOICE_SCREEN);
  React.useEffect(() => {
    GlobalPageLoadExperience.success({ force: true });
  }, []);

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;
  if (!data) {
    return (
      <ErrorComponents.ErrorPanel error={new ErrorComponents.NotFoundError(`Unable to find invoice with ID: ${id}`)} />
    );
  }

  const invoice = data.invoice as InvoiceWithLineItems;
  const adaBaseUrl = getAdaBaseURL();

  const auditTrailProps = {
    entityName: EntityWithNotes.INVOICE,
    id: invoice?.id,
    adaUrl: getViewInAdaInvoiceUrl(adaBaseUrl, id),
  };

  const allowedSidebars = {
    'pdf-link-pane': InvoicePdfLinkPane,
    'update-memo-pane': InvoiceUpdatePane,
    'resend-invoice-pane': InvoiceUpdatePane,
    'process-refund-ccp': InvoiceRefundCcpPane,
    'uncollectible-pane': InvoiceUncollectiblePane,
    'process-refund': ProcessRefundPane,
  };

  return (
    <BillingDocPage
      headerProps={{
        getIcon: getInvoiceIcon,
        getSubheader: getInvoiceSubheader,
        getHeader: getInvoiceHeader,
        getActions: getInvoiceActions,
        //viewers,
      }}
      onComplete={refetch}
      billingDoc={invoice}
      isFetchingBillingDoc={false}
      billingDocFetchError={null}
      getBreadcrumbTrail={getInvoiceBreadcrumbTrail}
      auditTrailProps={auditTrailProps}
      analyticsScreenSource={AnalyticsEventSource.INVOICE_SCREEN}
      additionalDetailsContent={getInvoiceAdditionalDetails(invoice)}
      BillingDocLineItems={InvoiceLineItems}
      allowedSidebars={allowedSidebars}
      paymentRetry={invoice.dunningStatus}
      fromCassi={true}
    />
  );
};

export default InvoicePage;
